import { type FC, useEffect, useState } from "react";
import { type BarDatum, ResponsiveBar } from "@nivo/bar";
import { isRecord } from "../../../../types/internal/utility";
import { useDarkMode } from "../../../Common/DarkMode/DarkModeHooks";
import { generateColourFromString } from "../../../Utilities/Generic/Colours";
import { currency, parseCurrency, roundTo } from "../../../Utilities/Generic/Numbers";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type RawReportData } from "../../../Utilities/Platform/Reports";
import { type DashboardReportType } from "../DashboardManager";

interface Props {
    report: DashboardReportType;
}

export const DashboardBar: FC<Props> = ({ report }) => {
    const [ invalid, setInvalid ] = useState(false);
    const [ minY, setMinY ] = useState(0);
    const [ maxY, setMaxY ] = useState(10);
    const [ data, setData ] = useState<Array<BarDatum>>([]);
    const [ keys, setKeys ] = useState<Array<string>>([]);
    const [ axisFormat, setAxisFormat ] = useState("");

    const darkMode = useDarkMode();

    useEffect(() => {
        const columns = [ ...report.data.columns ];

        const indexName = columns.findIndex(col => col === "Name");

        if(indexName < 0) {
            setInvalid(true);

            return;
        }

        setInvalid(false);

        columns.splice(indexName, 1);
        const nameColumn = report.data.columns[indexName] as keyof RawReportData;

        const keys = new Set<string>();
        const data: Array<BarDatum> = [];
        let minY = 0;
        let maxY = 0;
        let yAxisFormat = " ";

        for(const column of columns) {
            const colData: BarDatum = {
                id: column,
            };

            const rows = report.data.data as Array<RawReportData>;

            for(let i = 0, l = rows.length; i < l; i++) {
                const row = rows[i];

                const item = row[column as keyof RawReportData];

                let datum: number;

                if(isRecord(item) || typeof item === "undefined") {
                    datum = 0;
                } else if(typeof item === "string") {
                    datum = parseCurrency(item);
                    yAxisFormat = "$,.2f";
                } else {
                    datum = item as number;
                }

                if(datum < minY) minY = datum;

                if(datum > maxY) maxY = datum;

                const name = row[nameColumn] as string;

                keys.add(name);

                colData[name] = datum;
                colData[`${name}Color`] = generateColourFromString(name);
            }

            data.push(colData);
        }

        // NOTE: Looked at calculating using exponential, but it doesn't look any good
        //  e.g. $1151.94 as the max is really terrible.
        if(Math.abs(minY) < 10) {
            minY = roundTo(minY, 10, "down");
        } else {
            minY = roundTo(minY, 100, "down");
        }

        if(Math.abs(maxY) < 10) {
            maxY = roundTo(maxY, 10, "up");
        } else {
            maxY = roundTo(maxY, 100, "up");
        }

        setMinY(minY);
        setMaxY(maxY);
        setData(data);
        setKeys([ ...keys ]);
        setAxisFormat(yAxisFormat);
    }, [ report, darkMode ]);

    if(invalid) {
        return (
            <div className="do-not-print grow flex justify-center items-center text-lg">
                <FontAwesomeLight icon="chart-bar" className="mr-2" />
                Unexpected report data was received.
            </div>
        );
    }

    return (
        <ResponsiveBar
            data={data}
            minValue={minY}
            maxValue={maxY}
            keys={keys}
            padding={0.3}
            groupMode="grouped"
            colors={({ id, data }) => data[`${id}Color`] as string}
            animate={false}
            margin={{
                top   : 10,
                left  : 50,
                bottom: 50,
                right : 20,
            }}
            theme={{
                tooltip: {
                    container: {
                        background: darkMode ? "#000000" : "#FFFFFF",
                    },
                },
                labels: {
                    text: {
                        color: darkMode ? "#FFFFFF" : "#000000",
                    },
                },
                text: {
                    fill: darkMode ? "#FFFFFF" : "#000000",
                },
            }}
            axisBottom={{
                tickSize    : 5,
                tickPadding : 5,
                tickRotation: -30,
            }}
            axisLeft={{
                format: axisFormat,
            }}
            labelFormat={axisFormat}
            valueFormat={(v) => (axisFormat === " ") ? v.toString() : currency(v)}
        />
    );
};
